import React, { useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { useLocation, Link } from "react-router-dom";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useSelector } from "react-redux";
import {
    adminMenuItems,
    menuItems as sideMenuItems,
} from "../../data/menuItems";
import { Tooltip, Typography } from "@mui/material";
import Footer from "./Footer";
import { useResponsiveStyles } from "../../styles/useResponsiveStyles";

const Sidebar = ({ user }) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const location = useLocation();
    const menuType = useSelector((state) => state.menuType);
    const menuItems =
        menuType === "admin" && true ? adminMenuItems : sideMenuItems("admin");

    const toggleDrawer = () => {
        setOpen(!open);
    };

    const { getFontSize, getContainerWidth, getIconSize } =
        useResponsiveStyles();

    const sideBarHeadingStyles = getFontSize("side-bar-heading");
    const sideBarWidth = getContainerWidth("side-bar-width");

    const CustomListItemText = styled(ListItemText)(({ theme }) => ({
        "& .MuiTypography-root": {
            ...sideBarHeadingStyles,
        },
    }));

    const drawerWidth = sideBarWidth;

    const openedMixin = (theme) => ({
        width: drawerWidth,
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: "hidden",
    });

    const closedMixin = (theme) => ({
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: "hidden",
        width: `calc(${theme.spacing(7)} + 1px)`,
        [theme.breakpoints.up("sm")]: {
            width: `calc(${theme.spacing(7)} + 1px)`,
        },
    });

    const Drawer = styled(MuiDrawer, {
        shouldForwardProp: (prop) => prop !== "open",
    })(({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        ...(open && {
            ...openedMixin(theme),
            "& .MuiDrawer-paper": openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            "& .MuiDrawer-paper": closedMixin(theme),
        }),
    }));

    const ToggleButton = styled("div")(({ theme, open }) => ({
        ...getIconSize("toggle-icon"),
        position: "fixed",
        left: open ? drawerWidth - 14 : 32,
        zIndex: theme.zIndex.drawer + 3,
        transition: theme.transitions.create(["left"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        cursor: "pointer",
        backgroundImage: open
            ? `url(/icons/close-menu.svg)`
            : `url(/icons/open-menu.svg)`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        marginLeft: open ? theme.spacing(1) : theme.spacing(2),
        borderRadius: 4,
    }));

    return (
        <>
            <Drawer
                variant="permanent"
                open={open}
                sx={{
                    "& .MuiDrawer-paper": {
                        height: `calc(100% - ${
                            theme.mixins.toolbar.minHeight - 25
                        }px)`,
                        mt: `${theme.mixins.toolbar.minHeight - 25}px`,
                        border: "none",
                        boxShadow: "3.6px 0px 27px 0px #00000014",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#fff",
                    },
                }}
            >
                {/* <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        px: 2,
                        py: 1,
                        flexShrink: 0,
                    }}
                >
                    {user &&
                        getUserAvatar(
                            user?.profile_picture_url,
                            user?.username
                        )}
                    {open && (
                        <span className="ml-2 whitespace-nowrap text-base">
                            {user?.username}
                        </span>
                    )}
                </Box> */}
                <List
                    sx={{
                        overflowY: "auto",
                        flex: 1,
                        pb: 7,
                        "&::-webkit-scrollbar": {
                            width: "0px",
                        },
                    }}
                >
                    {Array.isArray(menuItems)
                        ? menuItems.map((item) => {
                              const isActive = location.pathname === item.path;
                              return (
                                  <ListItem
                                      key={item.text}
                                      disablePadding
                                      sx={{ display: "block" }}
                                  >
                                      <ListItemButton
                                          component={Link}
                                          to={item.path}
                                          selected={
                                              location.pathname === item.path
                                          }
                                          sx={{
                                              //   minHeight: 30,
                                              display: "flex",
                                              padding: "1px 5px",
                                              borderRadius: "5px",
                                              margin: "10px",
                                              "&:hover": {
                                                  backgroundColor:
                                                      "rgba(255, 0, 0, 0.04)",
                                              },
                                              ...(isActive && {
                                                  backgroundColor: "#EEF2F8",
                                                  "&:hover": {
                                                      backgroundColor:
                                                          "#EEF2F8",
                                                  },
                                              }),
                                          }}
                                      >
                                          <Tooltip
                                              title={open ? "" : item.text}
                                              arrow
                                              placement="right"
                                          >
                                              <ListItemIcon
                                                  sx={{
                                                      ...getIconSize(
                                                          "side-bar-icon"
                                                      ),
                                                      minWidth: 0,
                                                      mr: open ? 2 : "auto",
                                                      justifyContent: "center",
                                                  }}
                                                  className="flex-initial"
                                              >
                                                  <img
                                                      src={item.icon}
                                                      alt={item.text}
                                                      style={{
                                                          filter: isActive
                                                              ? "saturate(6932%) "
                                                              : "none",
                                                      }}
                                                  />
                                              </ListItemIcon>
                                          </Tooltip>
                                          {open && (
                                              <CustomListItemText
                                                  primary={item.text}
                                                  sx={{
                                                      color: isActive
                                                          ? "red"
                                                          : "#231F20",
                                                  }}
                                              />
                                          )}
                                      </ListItemButton>
                                  </ListItem>
                              );
                          })
                        : Object.entries(menuItems).map(([heading, items]) => (
                              <React.Fragment key={heading}>
                                  {Object.entries(menuItems).length > 1 &&
                                      items.length > 0 &&
                                      open && (
                                          <Typography
                                              sx={{
                                                  ...getFontSize("heading"),
                                                  px: 1,
                                                  color: "#C43A29",
                                                  ml: 1,
                                                  mt: 1.5,
                                                  //   fontSize: "1rem",
                                              }}
                                          >
                                              {heading}
                                          </Typography>
                                      )}
                                  {items.map((item) => {
                                      const isActive =
                                          location.pathname === item.path;
                                      return (
                                          <ListItem
                                              key={item.text}
                                              disablePadding
                                              sx={{ display: "block" }}
                                          >
                                              <ListItemButton
                                                  component={Link}
                                                  to={item.path}
                                                  selected={
                                                      location.pathname ===
                                                      item.path
                                                  }
                                                  sx={{
                                                      //   minHeight: 30,
                                                      display: "flex",
                                                      padding: "1px 5px",
                                                      borderRadius: "5px",
                                                      margin: "10px",
                                                      "&:hover": {
                                                          backgroundColor:
                                                              "rgba(255, 0, 0, 0.04)",
                                                      },
                                                      ...(isActive && {
                                                          backgroundColor:
                                                              "#EEF2F8",
                                                          "&:hover": {
                                                              backgroundColor:
                                                                  "#EEF2F8",
                                                          },
                                                      }),
                                                  }}
                                              >
                                                  <Tooltip
                                                      title={
                                                          open ? "" : item.text
                                                      }
                                                      arrow
                                                      placement="right"
                                                  >
                                                      <ListItemIcon
                                                          sx={{
                                                              ...getIconSize(
                                                                  "side-bar-icon"
                                                              ),
                                                              minWidth: 0,
                                                              mr: open
                                                                  ? 2
                                                                  : "auto",
                                                              justifyContent:
                                                                  "center",
                                                          }}
                                                          className="flex-initial"
                                                      >
                                                          <img
                                                              src={item.icon}
                                                              alt={item.text}
                                                              style={{
                                                                  filter: isActive
                                                                      ? "saturate(6932%) "
                                                                      : "none",
                                                              }}
                                                          />
                                                      </ListItemIcon>
                                                  </Tooltip>
                                                  {open && (
                                                      <CustomListItemText
                                                          primary={item.text}
                                                          sx={{
                                                              color: isActive
                                                                  ? "red"
                                                                  : "#231F20",
                                                          }}
                                                      />
                                                  )}
                                              </ListItemButton>
                                          </ListItem>
                                      );
                                  })}
                              </React.Fragment>
                          ))}
                </List>
                {open && <Footer />}
            </Drawer>
            <ToggleButton onClick={toggleDrawer} open={open} />
        </>
    );
};

export default Sidebar;
